import { HostInfo } from '@hqo/hqo-miniapp-client-sdk';
import { captureMessage } from '@sentry/react';
import { createContext, PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { useMiniappSdk } from 'app/shared/contexts/miniapp-sdk';
import { useMiniappSdkEventHandler } from 'app/shared/hooks/use-miniapp-sdk';
import { useContextValue } from 'app/shared/hooks/use-context-value';
import { HostPlatform } from 'app/shared/consts';

export interface HostInfoContextValue {
  hostPlatform?: HostPlatform;
  hostInfo?: HostInfo;
  /**
   * The host has answered — or there is no host to answer. Distinct from `hostInfo` being set, which
   * stays false forever on a malformed payload; anything waiting on that alone would never fire.
   */
  isHostInfoSettled: boolean;
}

const HostInfoContext = createContext<HostInfoContextValue>({ isHostInfoSettled: false });

/**
 * Fetches the host info once and exposes `hostPlatform` (mobile / web / tenant-portal), following the
 * miniapp-content pattern. Used to choose native (mobile) vs in-app (desktop) chrome — e.g. the booking
 * detail's back affordance.
 */
export const HostInfoProvider = ({ children }: PropsWithChildren): JSX.Element => {
  const [hostPlatform, setHostPlatform] = useState<HostPlatform>();
  const [hostInfo, setHostInfo] = useState<HostInfo>();
  const [isHostInfoSettled, setIsHostInfoSettled] = useState(false);
  const client = useMiniappSdk();

  useEffect(() => {
    if (client && !hostPlatform) {
      client.host.getInfo();
    } else if (!client) {
      setIsHostInfoSettled(true);
    }
  }, [client, hostPlatform]);

  useMiniappSdkEventHandler(
    'getHostInfoResponse',
    (data) => {
      setIsHostInfoSettled(true);
      // `getHostInfoResponse` should carry an object, but a non-empty string can slip through — e.g. a
      // double-encoded payload the SDK unwraps only once. `data &&` doesn't exclude a string, and
      // `'platform' in <string>` throws `Cannot use 'in' operator…`, so narrow to an object first.
      if (data && typeof data === 'object') {
        if ('platform' in data) {
          setHostPlatform(data.platform as HostPlatform);
        }
        setHostInfo(data as unknown as HostInfo);
      } else if (data != null) {
        captureMessage('getHostInfoResponse: unexpected non-object payload', {
          level: 'warning',
          extra: { payloadType: typeof data },
        });
      }
    },
    [setHostPlatform, setHostInfo, setIsHostInfoSettled],
  );

  const value = useMemo(
    () => ({ hostPlatform, hostInfo, isHostInfoSettled }),
    [hostPlatform, hostInfo, isHostInfoSettled],
  );

  return <HostInfoContext.Provider value={value}>{children}</HostInfoContext.Provider>;
};

export const useSdkHostInfo = (): HostInfoContextValue => useContextValue(HostInfoContext);

export { HostInfoContext };
